import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Handshake, TrendingUp, ShieldCheck, Users, ArrowRight, Loader2, CheckCircle2, Package, Wrench, Building2 } from "lucide-react";

const PARTNER_TYPES = [
  {
    id: "brand",
    icon: Package,
    title: "Brand / Manufacturer",
    desc: "List your dental brand and reach 15,000+ dental clinics across India.",
  },
  {
    id: "distributor",
    icon: Building2,
    title: "Distributor",
    desc: "Add your inventory catalogue and get access to bulk-buy demand.",
  },
  {
    id: "service",
    icon: Wrench,
    title: "Service Partner",
    desc: "Installation, AMC, refurbishment — plug your service into every order.",
  },
];

const PERKS = [
  { icon: TrendingUp, title: "Grow revenue faster", body: "Reach thousands of clinics with a single onboarding — no salesforce required." },
  { icon: ShieldCheck, title: "Reliable payments", body: "T+7 settlement with full GST invoicing and auto-reconciliation." },
  { icon: Users, title: "Marketing support", body: "Get featured across banners, newsletters, and Dr. Dyntra recommendations." },
  { icon: Handshake, title: "Dedicated account manager", body: "A named partner-success manager for every brand from day one." },
];

const initialForm = {
  company: "",
  contact_name: "",
  email: "",
  phone: "",
  partner_type: "brand",
  website: "",
  message: "",
};

export default function Partner() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company.trim() || !form.contact_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill in your company, name, email and phone");
      return;
    }
    setStatus("loading");
    try {
      try {
        await fetch("/api/partner/apply", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
      } catch { /* offline / mock — persist locally */ }
      const list = JSON.parse(localStorage.getItem("dyntra_partner_applications") || "[]");
      list.push({ ...form, submitted_at: new Date().toISOString() });
      localStorage.setItem("dyntra_partner_applications", JSON.stringify(list));
      setStatus("success");
      toast.success("Application received — our partner team will be in touch within 24 hours.");
      setForm(initialForm);
    } catch (err) {
      setStatus("idle");
      toast.error("Could not submit the application. Please try again.");
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#361D82] via-[#4B2AAD] to-[#6B4CD9] text-white">
        <div className="dyntra-container py-16 sm:py-20">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-white/70 mb-3">Partner Program</p>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Become a Dyntra Partner</h1>
            <p className="mt-4 text-lg text-white/85 max-w-2xl">
              Sell to India's largest network of verified dental clinics. Zero listing fees, transparent commissions, and a dedicated growth team behind every partner.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <a href="#apply" className="inline-flex items-center gap-2 rounded-xl bg-white text-[#4B2AAD] px-5 py-3 text-sm font-bold hover:bg-white/90 transition-colors shadow-lg shadow-black/10">
                Apply now <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/support" className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/10 backdrop-blur px-5 py-3 text-sm font-bold text-white hover:bg-white/20 transition-colors">
                Talk to us
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              <Stat label="Clinics" value="15,000+" />
              <Stat label="Cities" value="800+" />
              <Stat label="SKUs live" value="90,000+" />
            </div>
          </div>
        </div>
      </section>

      {/* Partner types */}
      <section className="dyntra-container py-14">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="font-display text-3xl font-bold text-gray-900">Who can partner with us</h2>
          <p className="text-gray-600 mt-2">Whether you make products, distribute them, or service them — there's a place for you on Dyntra.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PARTNER_TYPES.map((t) => {
            const Ico = t.icon;
            return (
              <div key={t.id} className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="h-11 w-11 rounded-xl bg-[#F3EFFC] flex items-center justify-center mb-3">
                  <Ico className="h-5 w-5 text-[#4B2AAD]" />
                </div>
                <h3 className="font-display text-lg font-bold text-gray-900">{t.title}</h3>
                <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{t.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Why partner */}
      <section className="bg-[#F8F9FA]">
        <div className="dyntra-container py-14">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-display text-3xl font-bold text-gray-900">Why leading brands choose Dyntra</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PERKS.map((p) => {
              const Ico = p.icon;
              return (
                <div key={p.title} className="flex items-start gap-4 rounded-2xl bg-white border border-gray-100 p-5">
                  <div className="h-10 w-10 rounded-xl bg-[#F3EFFC] flex items-center justify-center shrink-0">
                    <Ico className="h-5 w-5 text-[#4B2AAD]" />
                  </div>
                  <div>
                    <h4 className="font-display text-base font-bold text-gray-900">{p.title}</h4>
                    <p className="text-sm text-gray-600 mt-1 leading-relaxed">{p.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="apply" className="dyntra-container py-14">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-display text-3xl font-bold text-gray-900">Ready to grow with Dyntra?</h2>
            <p className="text-gray-600 mt-3">
              Fill in the form and our partner-success team will get in touch within 24 hours to walk you through onboarding.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-gray-700">
              {[
                "No listing fees, no lock-in",
                "GST-compliant invoicing out of the box",
                "Marketing spotlight for launch partners",
                "Live inventory sync + APIs available",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 rounded-2xl border border-gray-100 bg-white shadow-sm p-6 sm:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Company name*" testid="partner-company" value={form.company} onChange={(v) => set("company", v)} full />
              <Field label="Contact name*" testid="partner-contact-name" value={form.contact_name} onChange={(v) => set("contact_name", v)} />
              <Field label="Business email*" testid="partner-email" type="email" value={form.email} onChange={(v) => set("email", v)} />
              <Field label="Phone / WhatsApp*" testid="partner-phone" value={form.phone} onChange={(v) => set("phone", v)} />
              <Field label="Website" testid="partner-website" value={form.website} onChange={(v) => set("website", v)} />

              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Partner type</label>
                <div className="mt-1 grid grid-cols-3 gap-2">
                  {PARTNER_TYPES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`partner-type-${t.id}`}
                      onClick={() => set("partner_type", t.id)}
                      className={`rounded-xl border text-xs font-semibold py-2 px-2 transition-colors ${form.partner_type === t.id ? "border-[#4B2AAD] bg-[#F3EFFC] text-[#4B2AAD]" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}
                    >
                      {t.title}
                    </button>
                  ))}
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Tell us about your business</label>
                <textarea
                  data-testid="partner-message"
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  rows={4}
                  placeholder="Product categories, cities served, monthly volume, anything else we should know…"
                  className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-4 py-2.5 outline-none text-sm"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="partner-submit"
              disabled={status === "loading"}
              className="dyntra-btn-primary mt-5 w-full py-3 text-base"
            >
              {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit application <ArrowRight className="h-4 w-4" /></>}
            </button>
            {status === "success" && (
              <p data-testid="partner-success" className="mt-3 text-sm text-emerald-600 font-semibold text-center">Thanks — our team will reach out within 24 hours.</p>
            )}
          </form>
        </div>
      </section>
    </main>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="font-display text-2xl font-extrabold">{value}</p>
      <p className="text-xs uppercase tracking-wider text-white/70 font-semibold">{label}</p>
    </div>
  );
}

function Field({ label, testid, value, onChange, full, type = "text" }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-4 py-2.5 outline-none text-sm"
      />
    </div>
  );
}
